<script setup lang="ts">
import type { IconName } from "./icons";
import { computed } from "vue";
import { icons } from "./icons";

defineOptions({
  name: "VIcon",
});

/**
 * One icon from the icon register, drawn at the current font size (`1em`) in the current text color, so text classes
 * such as `text-sm` and `text-primary-500` size and color it. Pass `aria-label` or `title` for an icon that carries
 * meaning on its own; every other icon is hidden from assistive technology.
 */
const {
  name,
  spin = false,
  filled = false,
  strokeWidth = 2,
  size = "1em",
} = defineProps<{
  name: IconName
  /** Rotates the icon continuously, for a loader. */
  spin?: boolean
  /** Fills the closed shapes of the icon, e.g. a pressed pin or a chosen star. */
  filled?: boolean
  /** Line width in the 24-unit icon grid: 2 is the default, 1.5 a light icon. */
  strokeWidth?: number
  /** A number of pixels or a CSS length such as `1.25em`; `1em` follows the font size. */
  size?: number | `${number}${"em" | "rem" | "px" | "%"}`
}>();

const component = computed(() => icons[name]);
</script>

<template>
  <component
    :is="component"
    :width="size"
    :height="size"
    :stroke-width="strokeWidth"
    v-bind="filled ? { fill: 'currentColor' } : {}"
    :data-icon="name"
    class="inline-block shrink-0 align-[-0.125em]"
    :class="{ 'animate-spin': spin }"
  />
</template>
